import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, LifeBuoy, HelpCircle, Send, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FAQS = [
  {
    q: "What is Ascend Higher?",
    a: "A personal AI life coach that helps you organize your goals, habits, schedule, health, money, relationships, and next steps across 5 life pillars. It gives you a daily roadmap, remembers your context, and acts as an accountability partner.",
  },
  {
    q: "Is Ascend therapy or medical care?",
    a: "No. Ascend is for personal growth, organization, reflection, and habit-building only. It is not therapy, medical care, mental-health diagnosis or treatment, financial or legal advice, or crisis support. If you're in crisis, please call or text 988 (US).",
  },
  {
    q: "How much does it cost?",
    a: "Basic is $8.88/month or $98.88/year. Advanced is $28.88/month or $308.88/year. Every new user gets a 3-day free trial — no charge during the trial.",
  },
  {
    q: "What's the difference between Basic and Advanced?",
    a: "Basic gives you the daily AI roadmap, 5-pillar structure, habits, goals, check-ins, and the awareness practices library. Advanced adds unlimited coaching, voice conversations, persistent memory across sessions, weekly insights, proactive nudges, and the ability for your coach to adjust your roadmap mid-conversation.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel anytime from your account or by emailing support@ascendhigher.life. You'll keep access through the end of your current paid period.",
  },
  {
    q: "How do I get a refund?",
    a: "See our Refund Policy. Short version: cancel during the free trial and you pay nothing. Annual plans may be refunded pro-rata within 14 days. Billing errors are always refunded.",
  },
  {
    q: "Does Ascend keep my data private?",
    a: "Your data is yours. We don't sell personal information. Your conversations aren't used to train external AI models without your consent. You can delete your account and all data anytime.",
  },
  {
    q: "How does the AI remember me?",
    a: "On the Advanced plan, your coach remembers your 5-pillar snapshot, goals, habits, and past conversations across sessions. On Basic, each session starts with your snapshot but doesn't carry conversation history between chats.",
  },
  {
    q: "Can I choose my coach's personality?",
    a: "Yes. During onboarding you pick from Hard & Straight, Calm & Collected, No-BS Mentor, or Best Friend, plus male/female voice and American/British accent for voice coaching.",
  },
  {
    q: "Can I use it on my phone?",
    a: "Yes. Ascend is fully responsive and works on any modern browser. A native mobile app is coming soon.",
  },
];

export default function SupportPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/contact`, { email, message });
      toast.success("Got it — we'll reply within 2 business days.");
      setMessage("");
    } catch (err) {
      toast.error("Couldn't send. Email support@ascendhigher.life directly.");
    } finally {
      setSending(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API}/account/delete`);
      toast.success("Your account and all data have been deleted.");
      setTimeout(() => (window.location.href = "/"), 1500);
    } catch (err) {
      toast.error("Couldn't delete right now. Email support@ascendhigher.life.");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <div className="flex items-center gap-2 mb-3">
          <LifeBuoy size={16} className="text-[#486a8f]" />
          <p className="font-mono-eyebrow text-[#486a8f]">Support & Help</p>
        </div>
        <h1 className="font-display text-[40px] md:text-[52px] leading-[1.05] text-[#0f1724] tracking-tight mb-3">
          We're here to <span className="italic font-light text-[#486a8f]">help</span>.
        </h1>
        <p className="font-body text-[15px] text-[#4b5563] mb-10">
          Email us directly at{" "}
          <a href="mailto:support@ascendhigher.life" className="text-[#486a8f] underline font-medium">
            support@ascendhigher.life
          </a>{" "}or use the form below. We aim to reply within 2 business days.
        </p>

        <section className="mb-14">
          <div className="flex items-center gap-2 mb-5">
            <HelpCircle size={15} className="text-[#486a8f]" />
            <h2 className="font-display text-[26px] text-[#0f1724] tracking-tight">Frequently asked</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="group rounded-2xl bg-white border border-[#eadfc9] p-5 open:shadow-[0_10px_30px_-15px_rgba(15,23,36,0.15)]">
                <summary className="font-body font-medium text-[15px] text-[#0f1724] cursor-pointer flex items-start justify-between gap-4 list-none">
                  <span>{f.q}</span>
                  <span className="text-[#486a8f] shrink-0 transition-transform group-open:rotate-45 text-[20px] leading-none">+</span>
                </summary>
                <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-14 rounded-2xl bg-white border border-[#eadfc9] p-8">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={15} className="text-[#486a8f]" />
            <h2 className="font-display text-[22px] text-[#0f1724] tracking-tight">Contact us</h2>
          </div>
          <form onSubmit={submitContact} className="space-y-4">
            <div>
              <Label className="font-mono-eyebrow text-[#486a8f] mb-1.5 block">Your email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" required className="h-11" />
            </div>
            <div>
              <Label className="font-mono-eyebrow text-[#486a8f] mb-1.5 block">How can we help?</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Tell us what's going on..." required className="resize-none" />
            </div>
            <Button type="submit" disabled={sending} className="rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white h-11 px-6">
              <Send size={14} className="mr-2" /> {sending ? "Sending…" : "Send message"}
            </Button>
          </form>
        </section>

        {/* Crisis notice */}
        <section className="mb-14 rounded-2xl bg-[#f5e6e6] border border-[#e8c8c8] p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#b94e4e] mt-0.5 shrink-0" />
            <div>
              <p className="font-mono-eyebrow text-[#b94e4e] mb-1.5">Crisis or emergency</p>
              <p className="font-body text-[14px] text-[#0f1724] leading-relaxed">
                Ascend is not crisis support. If you are in danger or crisis, call or text{" "}
                <a href="tel:988" className="text-[#b94e4e] font-medium underline">988</a>{" "}(US) or your local emergency number.
                International options are available at <a className="text-[#b94e4e] font-medium underline" href="https://findahelpline.com" target="_blank" rel="noreferrer">findahelpline.com</a>.
              </p>
            </div>
          </div>
        </section>

        {/* Account deletion (App Store requirement) */}
        {user && (
          <section className="rounded-2xl bg-white border border-[#eadfc9] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={15} className="text-[#6b7280]" />
              <h2 className="font-display text-[20px] text-[#0f1724] tracking-tight">Delete your account</h2>
            </div>
            <p className="font-body text-[13px] text-[#4b5563] leading-relaxed mb-4">
              Permanently delete your account, roadmap, chat history, habits, goals, and all associated data.
              This cannot be undone.
            </p>
            {!confirmDelete ? (
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(true)}
                className="rounded-full border-[#b94e4e] text-[#b94e4e] hover:bg-[#f5e6e6]"
              >
                Delete my account
              </Button>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={deleteAccount}
                  disabled={deleting}
                  className="rounded-full bg-[#b94e4e] hover:bg-[#a33f3f] text-white"
                >
                  {deleting ? "Deleting…" : "Yes, permanently delete"}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(false)} className="rounded-full text-[#6b7280]">
                  Cancel
                </Button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
